import { useTranslation } from "react-i18next";
import { LegalModal } from "./LegalModal";
import { LegalSection } from "./LegalSection";

export const PrivacyPolicy = () => {
  const { t } = useTranslation();

  return (
    <LegalModal title={t("legal.privacy.title")}>
      <div className="space-y-6">
        {/* Metadata Header */}
        <div className="mb-8 p-4 bg-muted/30 rounded-lg space-y-2 text-sm">
          <p><strong>Effective Date:</strong> To be determined (Launch Date)</p>
          <p><strong>Last Updated:</strong> 14 October 2025</p>
          <p><strong>Company:</strong> Narrate BV t/a Momentum Lab</p>
          <p><strong>Contact:</strong> <a href="mailto:legal@momentumlab.earth" className="text-brand-teal hover:underline">legal@momentumlab.earth</a></p>
        </div>

        <LegalSection title={t("legal.privacy.section1.title")}>
          <p>{t("legal.privacy.section1.content")}</p>
        </LegalSection>

        <LegalSection title={t("legal.privacy.section2.title")}>
          <p>{t("legal.privacy.section2.content")}</p>
          <p>We collect information you provide directly to us, including:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Name and contact information (email, company details)</li>
            <li>Professional information (job title, company size)</li>
            <li>Communication preferences and challenges you share</li>
            <li>Device and browser information for analytics</li>
          </ul>
        </LegalSection>

        <LegalSection title={t("legal.privacy.section3.title")}>
          <p>{t("legal.privacy.section3.content")}</p>
          <p>We use the collected information to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Provide and improve our services</li>
            <li>Communicate with you about our platform</li>
            <li>Analyse usage patterns and optimise user experience</li>
            <li>Comply with legal obligations</li>
          </ul>
        </LegalSection>

        <LegalSection title="Third-Party Services">
          <p>We use the following third-party services to operate our platform:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>HubSpot:</strong> Customer relationship management and email communications</li>
            <li><strong>PostHog:</strong> Product analytics and user behaviour tracking</li>
            <li><strong>Sentry:</strong> Error tracking and performance monitoring</li>
            <li><strong>Google Tag Manager:</strong> Tag management and analytics integration</li>
          </ul>
          <p>Each service has its own privacy policy governing the use of your data.</p>
        </LegalSection>

        <LegalSection title="Data Retention">
          <p>We retain your personal data for as long as necessary to provide our services and comply with legal obligations. Specifically:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Waitlist data:</strong> Until platform launch or upon request for deletion</li>
            <li><strong>Analytics data:</strong> Aggregated and anonymised after 24 months</li>
            <li><strong>Communication records:</strong> Retained for 7 years for business purposes</li>
          </ul>
        </LegalSection>

        <LegalSection title="International Data Transfers">
          <p>Your data may be transferred to and processed in countries outside the European Economic Area (EEA). We ensure appropriate safeguards are in place, including:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Standard Contractual Clauses approved by the European Commission</li>
            <li>Adequacy decisions recognising equivalent data protection</li>
            <li>Vendor compliance with privacy frameworks (Privacy Shield successor)</li>
          </ul>
        </LegalSection>

        <LegalSection title="Data Breach Notification">
          <p>In the event of a data breach affecting your personal information, we will:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Notify affected individuals within 72 hours of discovery</li>
            <li>Report the breach to relevant supervisory authorities</li>
            <li>Provide details on the nature of the breach and remediation steps</li>
            <li>Offer guidance on protecting yourself from potential harm</li>
          </ul>
        </LegalSection>

        <LegalSection title="Children's Privacy">
          <p>Our services are not intended for individuals under the age of 16. We do not knowingly collect personal information from children. If we become aware that we have collected data from a child, we will take steps to delete such information promptly.</p>
        </LegalSection>

        <LegalSection title={t("legal.privacy.section4.title")}>
          <p>{t("legal.privacy.section4.content")}</p>
          <p>Under GDPR and other privacy laws, you have the right to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Access your personal data</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Object to processing of your data</li>
            <li>Request data portability</li>
            <li>Withdraw consent at any time</li>
          </ul>
          <p>To exercise these rights, please contact us at <a href="mailto:legal@momentumlab.earth" className="text-brand-teal hover:underline">legal@momentumlab.earth</a>.</p>
        </LegalSection>

        {/* Contact Footer */}
        <div className="mt-8 pt-6 border-t border-border text-center">
          <p className="text-foreground/70 tracking-wide">
            Questions about this policy? Contact us at{' '}
            <a href="mailto:legal@momentumlab.earth" className="text-brand-teal hover:underline">
              legal@momentumlab.earth
            </a>
          </p>
        </div>
      </div>
    </LegalModal>
  );
};
